import { Component, EventEmitter, Input, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgSelectModule } from '@ng-select/ng-select';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ModelService } from '../../../../core/services/model/model.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { FormatDatePipe } from '../../../../core/pipe/custom-pipes';
import { copyToClipboard as copyToClipboardUtil } from '../../../../core/utils/clipboard.utils';
import { environment } from '../../../../../environments/environment';
import { AuthStateService } from '../../../../core/services/auth-state/auth-state-service.service';

@Component({
  selector: 'app-delivery-form',
  standalone: true,
  imports: [ReactiveFormsModule,
    TranslateModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    CommonModule,
    NgSelectModule,
    FormatDatePipe,
    GoogleMapsModule],
  templateUrl: './delivery-form.component.html',
  styleUrl: './delivery-form.component.css'
})
export class DeliveryFormComponent implements OnDestroy {
  @Input() orderData?: any;
  @Input() ladata: any = [];

  @Output() emitOpenAddressesForm = new EventEmitter<boolean>();

  @ViewChild(GoogleMap) googleMap?: GoogleMap;

  deliveryForm: FormGroup;
  delivery_place: any[] = [];
  delivery_status: any[] = [];
  delivery_dealer: any[] = [];
  delivery_urgency_: any[] = ['high', 'medium', 'low', 'custom'];
  workshopData: any[] = [];
  logisticData: any[] = [];

  // Google Maps
  mapCenter: google.maps.LatLngLiteral = { lat: 40.0, lng: -3.7 };
  mapZoom = 5;
  markerPosition: google.maps.LatLngLiteral | null = null;
  mapOptions: google.maps.MapOptions = {
    minZoom: 3,
    maxZoom: 18,
    streetViewControl: false,
    mapTypeControl: false,
  };
  mapReady = false;
  isSupplier = false;
  mapSectionOpen = false;
  mapEditable = false;

  private addressSearch$ = new Subject<void>();
  private addressSearchSub?: Subscription;

  constructor(private fb: FormBuilder, private modelService: ModelService, private alertService: AlertService, private authStateService: AuthStateService) {

    this.deliveryForm = this.fb.group({
      delivery_address: ['', Validators.required],
      delivery_postal_code: ['', Validators.required],
      delivery_contact: ['', Validators.required],
      delivery_phone: ['', Validators.required],
      delivery_city: ['', Validators.required],
      delivery_place: [''],
      delivery_dealer: ['', Validators.required],
      delivery_first_expected_date: ['', Validators.required],
      delivery_expected_date: ['', Validators.required],
      delivery_date: ['', Validators.required],
      delivery_delay_notes: ['', Validators.required],
      delivery_urgency: [''],
      delivery_client_expected_date: ['', Validators.required],
      delivery_appointment_date: ['', Validators.required],
      logistic_reference: ['', Validators.required],
      logistic_id: [''],
      logistic_comments: [''],
      delivery_state: [''],
      delivery_latitude: [null],
      delivery_longitude: [null],
    });
  }

  ngAfterViewInit() {

  }

  ngOnInit(): void {
    this.isSupplier = this.authStateService.getAccountType() === 'supplier';
    this.addressSearchSub = this.addressSearch$
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe(() => this.geocodeAddress());
  }

  ngOnDestroy(): void {
    this.addressSearchSub?.unsubscribe();
    this.addressSearch$.complete();
  }

  private loadGoogleMapsScript(): void {
    if (typeof google !== 'undefined' && google.maps) {
      this.mapReady = true;
      return;
    }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      existing.addEventListener('load', () => this.mapReady = true);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => this.mapReady = true;
    document.head.appendChild(script);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes['ladata']) {
        this.delivery_place = this.ladata.delivery_place;
        this.delivery_status = this.ladata.delivery_status;
        this.workshopData = this.ladata.workshopData;
        this.logisticData = this.ladata.logisticData;
        if (this.orderData) {
          this.refrescarFormulario();
        }
      }

      if (changes['orderData']) {
        if (this.orderData) {
          this.refrescarFormulario();
        }
      }
    }
  }

  refrescarFormulario() {
    this.updateFieldAccessibility();
    for (const key in this.orderData) {
      const control = this.deliveryForm.controls[key];
      const value = this.orderData[key];
      if (control) {
        control.setValue(value);
      }
    }
    this.alertService.close();

    if (this.mapEditable) {
      this.updateMapFromForm();

      // If no lat/lng but address data exists, trigger geocode after form load
      const lat = this.deliveryForm.get('delivery_latitude')?.value;
      const lng = this.deliveryForm.get('delivery_longitude')?.value;
      if (!lat && !lng) {
        this.addressSearch$.next();
      }
    }
  }

  updateFieldAccessibility() {
    var rules = this.orderData?.rules;
    // Deshabilitar todos los campos por defecto
    for (const field in this.deliveryForm.controls) {
      if (this.deliveryForm.controls.hasOwnProperty(field)) {
        this.deliveryForm.get(field)?.disable();
      }
    }

    // Habilitar solo los campos con valor 'w' en las reglas
    for (const field in rules) {
      if (rules.hasOwnProperty(field) && rules[field] === 'w') {
        this.deliveryForm.get(field)?.enable();
      }
    }

    this.mapEditable = rules?.delivery_latitude === 'w' || rules?.delivery_longitude === 'w';

    // Load Google Maps script only when the map is actually needed
    if (this.isSupplier && this.mapEditable) {
      this.loadGoogleMapsScript();
    }
  }

  toggleMapSection(): void {
    this.mapSectionOpen = !this.mapSectionOpen;
    if (this.mapSectionOpen && this.mapEditable) {
      setTimeout(() => {
        if (this.googleMap?.googleMap) {
          google.maps.event.trigger(this.googleMap.googleMap, 'resize');
          this.googleMap.googleMap.setCenter(this.mapCenter);
        }

        // Auto-geocode if no coordinates but address data exists
        const lat = this.deliveryForm.get('delivery_latitude')?.value;
        const lng = this.deliveryForm.get('delivery_longitude')?.value;
        if (!lat && !lng) {
          this.geocodeAddress();
        }
      }, 100);
    }
  }

  onMapClick(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) return;
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    this.markerPosition = { lat, lng };
    this.deliveryForm.patchValue({
      delivery_latitude: lat,
      delivery_longitude: lng,
    });
    this.deliveryForm.markAsDirty();
  }

  onCoordsBlur(): void {
    if (!this.mapEditable) return;
    this.updateMapFromForm();
  }

  onAddressBlur(): void {
    if (!this.mapEditable) return;
    this.addressSearch$.next();
  }

  onAddressInput(): void {
    if (!this.mapEditable) return;
    this.addressSearch$.next();
  }

  geocodeAddress(): void {
    if (!this.mapEditable) return;
    const postalCode = this.deliveryForm.get('delivery_postal_code')?.value;
    const city = this.deliveryForm.get('delivery_city')?.value;
    const address = this.deliveryForm.get('delivery_address')?.value;
    const query = [address, postalCode, city].filter(Boolean).join(', ');
    if (!query || !this.mapReady) return;

    const fullQuery = query + ', España';
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: fullQuery }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        this.markerPosition = { lat, lng };
        this.mapCenter = { lat, lng };
        this.mapZoom = 14;
        this.deliveryForm.patchValue({
          delivery_latitude: lat,
          delivery_longitude: lng,
        });
        this.deliveryForm.markAsDirty();
      }
    });
  }

  private updateMapFromForm(): void {
    const lat = parseFloat(this.deliveryForm.get('delivery_latitude')?.value);
    const lng = parseFloat(this.deliveryForm.get('delivery_longitude')?.value);

    if (!isNaN(lat) && !isNaN(lng)) {
      this.markerPosition = { lat, lng };
      this.mapCenter = { lat, lng };
      this.mapZoom = 14;
    } else {
      this.markerPosition = null;
      this.mapCenter = { lat: 40.0, lng: -3.7 };
      this.mapZoom = 5;
    }
  }

  openAddressesForm() {
    this.emitOpenAddressesForm.emit(true);
  }

  checkUpdateForm(): boolean {
    if (this.deliveryForm.dirty) {
      return true;
    } else {
      return false;
    }
  }
  copyToClipboard(value: string, event: Event): void {
    copyToClipboardUtil(value, event);
  }

}
